import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { CtaButton, type CtaSize, type CtaVariant } from '@/components/ui';
import { BunnyVideo } from './BunnyVideo';
import styles from './VideoDialog.module.css';

export interface VideoDialogProps {
  /** Rótulo del botón que abre el vídeo. */
  label: string;
  /** GUID del vídeo en Bunny Stream. */
  videoId: string;
  /** Título del diálogo. Es lo que anuncia el lector de pantalla al abrirse. */
  title: string;
  /** Qué se ve, para quien no puede verlo. */
  description: string;
  variant?: CtaVariant;
  size?: CtaSize;
  icon?: ReactNode;
  className?: string;
}

/**
 * BOTÓN + VÍDEO EN MODAL — encargo del cliente (12-sep-2026).
 *
 * EL VÍDEO NO VIVE EN ESTE REPO, VIVE EN BUNNY STREAM. Decisión del ORACLE del
 * 12-sep-2026 («mejor desde allí, no desde nuestra plataforma»): transcodificado
 * a 240p…1080p, replicado en NY, LA y São Paulo —el 92 % de los compradores son
 * de Latinoamérica— y fuera de la imagen de Docker.
 *
 * PERO EL REPRODUCTOR ES NUESTRO, NO EL IFRAME DE BUNNY, Y ESA ES LA CLAVE.
 * Con el `<iframe>` el vídeo NO podía arrancar solo con sonido, y no por capricho
 * del navegador: la política de autoarranque mira si HAY GESTO DEL USUARIO EN
 * ESE DOCUMENTO, y un iframe de otro origen recién nacido es otro documento que
 * jamás recibió el clic. Con un `<video>` en NUESTRA página el clic que abre el
 * diálogo es el gesto, así que `play()` con sonido está permitido. Encargo del
 * ORACLE (12-sep-2026): «¿por qué no se reproduce automáticamente con sonido?».
 *
 * Y de paso desaparece la espera en azul: el `poster` de Bunny pinta el primer
 * fotograma al instante mientras el vídeo arranca.
 *
 * CÓMO SE ELIGE LA CALIDAD, sin una sola dependencia:
 *   1. `playlist.m3u8` primero — HLS adaptativo de verdad. Safari e iOS lo
 *      reproducen nativo, y son justo los que peor llevan un MP4 gigante.
 *   2. MP4 de respaldo para Chrome/Android, que no leen HLS sin una librería de
 *      40 KB: el navegador descarta la fuente que no sabe tocar y baja a ésta
 *      él solo, sin JS. 480p en móvil (28 MB) y 720p en escritorio (48 MB).
 *
 * POR QUÉ UN `<dialog>` NATIVO Y NO UNA LIBRERÍA DE MODALES. `showModal()` ya
 * trae captura del foco, cierre con `Esc`, `inert` sobre el resto del documento
 * y `::backdrop`. Una dependencia aquí sería cambiar 40 líneas por 40 KB de JS.
 *
 * EL VÍDEO SE MONTA AL ABRIR Y SE DESMONTA AL CERRAR. Quien no pulsa no descarga
 * un solo byte, y al cerrar el audio para en seco.
 */
export function VideoDialog({
  label,
  videoId,
  title,
  description,
  variant = 'gold',
  size = 'lg',
  icon,
  className,
}: VideoDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [abierto, setAbierto] = useState(false);
  const titleId = useId();

  /* El estado de React manda sobre el diálogo, no al revés: `showModal()` es
   * imperativo y sin este efecto los dos se desincronizan al primer `Esc`. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (abierto && !dialog.open) dialog.showModal();
    if (!abierto && dialog.open) dialog.close();
  }, [abierto]);

  /**
   * ARRANQUE CON SONIDO. El atributo `autoplay` solo no basta en todos los
   * navegadores cuando el elemento acaba de montarse, así que se pide también a
   * mano. Si aun así lo rechazan —un móvil en modo ahorro, una preferencia del
   * sistema—, NO se recurre a silenciarlo: esto es alguien HABLANDO y sin voz no
   * hay vídeo. Se queda el fotograma de portada con los controles, que es un
   * estado honesto: el visitante pulsa «play» y suena.
   */
  useEffect(() => {
    if (!abierto) return;
    videoRef.current?.play().catch(() => {
      /* Rechazo de la política de autoarranque: los controles siguen ahí. */
    });
  }, [abierto]);

  /**
   * LA CAJA SE AJUSTA AL VÍDEO, NO AL REVÉS. El marco era 16:9 fijo y el vídeo
   * de Palmaïa está grabado EN VERTICAL: se veía una cinta estrecha en medio de
   * dos columnas negras que ocupaban más pantalla que la imagen. La proporción
   * real no se sabe hasta que llegan los metadatos, así que se lee de ahí y se
   * aplica al contenedor. Mientras no ha llegado, manda el 16:9 del CSS, que es
   * lo que acierta en cuatro de los seis vídeos del cliente.
   */
  const [proporcion, setProporcion] = useState<number | null>(null);

  useEffect(() => {
    if (!abierto) {
      setProporcion(null);
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    const leer = () => {
      if (video.videoWidth > 0) setProporcion(video.videoWidth / video.videoHeight);
    };
    leer();
    video.addEventListener('loadedmetadata', leer);
    return () => video.removeEventListener('loadedmetadata', leer);
  }, [abierto]);

  const cerrar = () => setAbierto(false);
  const abrir = () => setAbierto(true);

  return (
    <>
      <CtaButton variant={variant} size={size} icon={icon} className={className} onClick={abrir}>
        {label}
      </CtaButton>

      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby={titleId}
        onClose={cerrar}
        /* Pulsar el fondo cierra. El `<dialog>` ocupa toda la ventana, así que
         * el clic en el backdrop llega con `target` === el propio diálogo. */
        onClick={(event) => {
          if (event.target === dialogRef.current) cerrar();
        }}
      >
        <div className={styles.marco}>
          <div className={styles.barra}>
            <h2 className={styles.titulo} id={titleId}>
              {title}
            </h2>
            <button className={styles.cerrar} type="button" onClick={cerrar}>
              <span aria-hidden="true">✕</span>
              <span className="az-visually-hidden">Cerrar el vídeo</span>
            </button>
          </div>

          {abierto ? (
            <div
              className={[styles.marcoVideo, proporcion && proporcion < 1 ? styles.marcoVertical : null]
                .filter(Boolean)
                .join(' ')}
              style={proporcion ? { aspectRatio: String(proporcion) } : undefined}
            >
              <BunnyVideo
                ref={videoRef}
                videoId={videoId}
                description={description}
                autoPlay
                className={styles.video}
              />
            </div>
          ) : null}
        </div>
      </dialog>
    </>
  );
}
